"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { getGraph, searchGraphNodes } from "@/lib/api";
import type { GraphData } from "@/lib/types";
import { Loader2 } from "lucide-react";

const KnowledgeGraph = dynamic(
  () => import("@/components/graph/KnowledgeGraph"),
  { ssr: false }
);

export default function GraphPage() {
  const [data, setData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getGraph()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleSearch(query: string) {
    try {
      setLoading(true);
      const result = await searchGraphNodes(query);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: "calc(100vh - 64px)", background: "#0a0e1a" }}
      >
        <div className="text-center">
          <p style={{ color: "#e8e0d0" }} className="text-lg mb-2">
            Failed to load graph
          </p>
          <p style={{ color: "rgba(232, 224, 208, 0.5)" }} className="text-sm">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "calc(100vh - 64px)", background: "#0a0e1a" }} className="relative">
      {loading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#0a0e1a]/80">
          <div className="flex items-center gap-3">
            <Loader2
              size={20}
              className="animate-spin"
              style={{ color: "#c8a45e" }}
            />
            <span style={{ color: "#e8e0d0" }} className="text-sm">
              Loading knowledge graph...
            </span>
          </div>
        </div>
      )}
      {data && <KnowledgeGraph data={data} onSearch={handleSearch} />}
    </div>
  );
}
